<script setup lang="ts">
import { computed } from 'vue';

import { BaseButton, BaseIcon } from '@magma-app/magma-lapilli';

import ManageRewardsHeroIcon from './ManageRewardsHeroIcon.vue';
import type { ManageRewardsItem } from './types';

const props = defineProps<{
  item: ManageRewardsItem;
  title?: string | null;
  description?: string | null;
  pointEnabled?: boolean;
  balance?: number | null;
  isSaving: boolean;
}>();

const emit = defineEmits<{
  (event: 'confirm'): void;
  (event: 'close'): void;
}>();

const resolvedTitle = computed(() => props.title || props.item.name);
const resolvedDescription = computed(
  () => props.description || props.item.description
);

const showSummary = computed(() => props.pointEnabled && !!props.balance);
const remainingBalance = computed(() =>
  props.balance ? props.balance - props.item.points : 0
);
</script>

<template>
  <div>
    <div class="mb-4 flex justify-between">
      <ManageRewardsHeroIcon variant="purchase" />

      <BaseIcon
        icon="close"
        color="#5F6C85"
        hover-state
        @click="emit('close')"
      />
    </div>

    <h3 class="mb-2 text-mgm-txt-lg font-semibold text-foreground-emphasis">
      {{ resolvedTitle }}
    </h3>

    <div v-if="showSummary" class="mb-6">
      <div
        class="mb-2 flex justify-between text-mgm-txt-sm font-normal text-foreground-default"
      >
        <span>{{ $t('manageRewardsModal.purchaseModal.points') }}</span>
        <span>{{ balance }}</span>
      </div>

      <div
        class="mb-2 flex justify-between text-mgm-txt-sm font-normal text-foreground-default"
      >
        <span>{{ $t('manageRewardsModal.purchaseModal.cost') }}</span>
        <span>{{ item.points }}</span>
      </div>

      <div
        v-if="balance"
        class="flex justify-between border-t border-border-subtle pt-2 text-mgm-txt-sm font-medium text-foreground-emphasis"
      >
        <span class="mr-8">{{
          $t('manageRewardsModal.purchaseModal.balance')
        }}</span>
        <span>{{ remainingBalance }}</span>
      </div>
    </div>

    <BaseButton
      class="mb-2 w-full"
      :disabled="isSaving"
      :loading="isSaving"
      :text="$t('generic.confirm')"
      @click="emit('confirm')"
    />

    <BaseButton
      class="w-full"
      state="scdr"
      :text="$t('generic.cancel')"
      data-cy="btn-close-modal"
      @click="emit('close')"
    />
  </div>
</template>
